/**
 * Google Ads Helper Functions
 * Utilities for handling Google Ads API data types and conversions
 */

/**
 * Converts dollars to micros (required by Google Ads API)
 * 
 * Google Ads API uses micros for all monetary values:
 * 1 EUR = 1.000.000 Micros
 * 
 * @example
 * amountToMicros(10.50) // Returns: 10500000
 * amountToMicros(100) // Returns: 100000000
 */
export function amountToMicros(amount: number): number {
  return Math.round(amount * 1000000);
}

/**
 * Converts micros to dollars
 * 
 * @example
 * microsToAmount(10500000) // Returns: 10.50
 * microsToAmount(100000000) // Returns: 100
 */
export function microsToAmount(micros: number): number {
  return micros / 1000000;
}

/**
 * Validates cost_micros is processed correctly
 * 
 * @param costMicros The cost in micros from Google Ads API
 * @returns Validation result with converted amount
 */
export function validateCostMicros(costMicros: number): {
  isValid: boolean;
  amount: number;
  warning?: string;
} {
  if (costMicros < 0) {
    return { 
      isValid: false, 
      amount: 0, 
      warning: 'Negative cost detected' 
    };
  }
  
  if (costMicros > 1000000000000) { // > 1 Million €
    return {
      isValid: true,
      amount: microsToAmount(costMicros),
      warning: 'Unusually high cost detected'
    };
  }
  
  return { 
    isValid: true, 
    amount: microsToAmount(costMicros) 
  };
}

/**
 * Format date for Google Ads API (YYYYMMDD)
 * 
 * @example
 * formatDateForAPI(new Date('2024-01-15')) // Returns: '20240115'
 */
export function formatDateForAPI(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
}

/**
 * Parse date from Google Ads API format (YYYY-MM-DD)
 */
export function parseDateFromAPI(dateString: string): Date {
  return new Date(dateString);
}

/**
 * Validate customer ID format
 * Google Ads customer IDs should be 10 digits (with or without dashes)
 */
export function validateCustomerId(customerId: string): {
  isValid: boolean;
  cleanId: string;
  error?: string;
} {
  const clean = customerId.replace(/-/g, '');
  
  if (!/^\d{10}$/.test(clean)) {
    return {
      isValid: false,
      cleanId: clean,
      error: 'Customer ID must be 10 digits'
    };
  }
  
  return {
    isValid: true,
    cleanId: clean
  };
}

/**
 * Format customer ID for display (XXX-XXX-XXXX)
 */
export function formatCustomerIdForDisplay(customerId: string): string {
  const clean = customerId.replace(/-/g, '');
  if (clean.length === 10) {
    return `${clean.slice(0, 3)}-${clean.slice(3, 6)}-${clean.slice(6)}`;
  }
  return customerId;
}

/**
 * Validate resource name format
 * Format: customers/{customer_id}/resourceType/{resource_id}
 */
export function validateResourceName(resourceName: string): {
  isValid: boolean;
  customerId?: string;
  resourceType?: string;
  resourceId?: string;
  error?: string;
} {
  const pattern = /^customers\/(\d+)\/([a-zA-Z]+)\/(\d+)$/;
  const match = resourceName.match(pattern);
  
  if (!match) {
    return {
      isValid: false,
      error: 'Invalid resource name format'
    };
  }
  
  return {
    isValid: true,
    customerId: match[1],
    resourceType: match[2],
    resourceId: match[3]
  };
}

/**
 * Calculate CTR (Click-Through Rate)
 */
export function calculateCTR(clicks: number, impressions: number): number {
  if (impressions === 0) return 0;
  return (clicks / impressions) * 100;
}

/**
 * Calculate CPA (Cost Per Acquisition)
 */
export function calculateCPA(costMicros: number, conversions: number): number {
  if (conversions === 0) return 0;
  return microsToAmount(costMicros) / conversions;
}

/**
 * Calculate ROAS (Return on Ad Spend)
 */
export function calculateROAS(conversionValue: number, costMicros: number): number {
  const cost = microsToAmount(costMicros);
  if (cost === 0) return 0;
  return conversionValue / cost;
}

/**
 * Validate headline length (max 30 chars for Search Ads)
 */
export function validateHeadline(headline: string): {
  isValid: boolean;
  length: number;
  error?: string;
} {
  const length = headline.length;
  
  if (length === 0) {
    return { isValid: false, length, error: 'Headline cannot be empty' };
  }
  
  if (length > 30) {
    return { isValid: false, length, error: 'Headline exceeds 30 characters' };
  }
  
  return { isValid: true, length };
}

/**
 * Validate description length (max 90 chars for Search Ads)
 */
export function validateDescription(description: string): {
  isValid: boolean;
  length: number;
  error?: string;
} {
  const length = description.length;
  
  if (length === 0) {
    return { isValid: false, length, error: 'Description cannot be empty' };
  }
  
  if (length > 90) {
    return { isValid: false, length, error: 'Description exceeds 90 characters' };
  }
  
  return { isValid: true, length };
}

/**
 * Truncate headline to max 30 characters
 * Truncates at word boundary for clean, professional appearance
 * Does NOT add "..." - keeps headline clean and complete-looking
 */
export function truncateHeadline(headline: string): string {
  if (headline.length <= 30) return headline;
  
  // First try to find a clean word boundary within 30 chars
  const truncated = headline.substring(0, 30);
  const lastSpace = truncated.lastIndexOf(' ');
  
  // If we found a space, truncate there for a clean word ending
  if (lastSpace > 15) {
    return truncated.substring(0, lastSpace).trim();
  }
  
  // No good word boundary - hard truncate at 30 chars (no "...")
  // This keeps the headline at exactly the limit without ugly ellipsis
  return truncated.trim();
}

/**
 * Truncate description to max 90 characters
 * Truncates at word boundary for clean, professional appearance
 * Does NOT add "..." - keeps description clean and complete-looking
 */
export function truncateDescription(description: string): string {
  if (description.length <= 90) return description;
  
  // First try to find a clean word boundary within 90 chars
  const truncated = description.substring(0, 90);
  const lastSpace = truncated.lastIndexOf(' ');
  
  // If we found a space, truncate there for a clean word ending
  if (lastSpace > 70) {
    return truncated.substring(0, lastSpace).trim();
  }
  
  // No good word boundary - hard truncate at 90 chars (no "...")
  return truncated.trim();
}

/**
 * Validate callout text (max 25 chars)
 */
export function validateCallout(callout: string): {
  isValid: boolean;
  length: number;
  error?: string;
} {
  const length = callout.length;
  
  if (length === 0) {
    return { isValid: false, length, error: 'Callout cannot be empty' };
  }
  
  if (length > 25) {
    return { isValid: false, length, error: 'Callout exceeds 25 characters' };
  }
  
  return { isValid: true, length };
}

/**
 * Validate sitelink text (max 25 chars)
 */
export function validateSitelinkText(text: string): {
  isValid: boolean;
  length: number;
  error?: string;
} {
  const length = text.length;
  
  if (length === 0) {
    return { isValid: false, length, error: 'Sitelink text cannot be empty' };
  }
  
  if (length > 25) {
    return { isValid: false, length, error: 'Sitelink text exceeds 25 characters' };
  }
  
  return { isValid: true, length };
}

/**
 * Validate sitelink description (max 35 chars)
 */
export function validateSitelinkDescription(description: string): {
  isValid: boolean;
  length: number;
  error?: string;
} {
  const length = description.length;
  
  if (length === 0) {
    return { isValid: false, length, error: 'Description cannot be empty' };
  }
  
  if (length > 35) {
    return { isValid: false, length, error: 'Description exceeds 35 characters' };
  }
  
  return { isValid: true, length };
}

/**
 * Get location ID from location name
 * Common locations mapped to Google Ads geo target constants
 */
export function getLocationId(locationName: string): string {
  const locationMap: Record<string, string> = {
    'United States': '2840',
    'Germany': '2276',
    'United Kingdom': '2826',
    'Austria': '2040',
    'Switzerland': '2756',
    'Canada': '2124',
    'France': '2250',
    'Spain': '2724',
    'Italy': '2380',
    'Netherlands': '2528',
    'Belgium': '2056',
    'Sweden': '2752',
    'Norway': '2578',
    'Denmark': '2208',
    'Finland': '2246',
    'Poland': '2616',
    'Australia': '2036',
    'New Zealand': '2554',
    'Japan': '2392',
    'South Korea': '2410',
    'Singapore': '2702',
    'India': '2356',
    'Brazil': '2076',
    'Mexico': '2484'
  };
  
  return locationMap[locationName] || '2840'; // Default to US
}

/**
 * Get language ID from language name
 */
export function getLanguageId(languageName: string): string {
  const languageMap: Record<string, string> = {
    'English': '1000',
    'German': '1001',
    'French': '1002',
    'Spanish': '1003',
    'Italian': '1004',
    'Dutch': '1010',
    'Portuguese': '1014',
    'Swedish': '1020',
    'Norwegian': '1013',
    'Danish': '1009',
    'Finnish': '1011',
    'Polish': '1015',
    'Japanese': '1005',
    'Korean': '1012',
    'Chinese (Simplified)': '1017',
    'Chinese (Traditional)': '1018'
  };
  
  return languageMap[languageName] || '1000'; // Default to English
}

